import React, { useState } from 'react';
import { Sidebar } from '@crisellevaldez/speculo-ui';
import { Users, Repeat, Zap } from 'lucide-react';
import { PageLayout } from '../components/common/layout/PageLayout';
import EngageFlowPage from './flows/engage';
import ConvertFlowPage from './flows/convert';
import ReviveFlowPage from './flows/revive';

type FlowType = 'engage' | 'convert' | 'revive';

/**
 * FlowsPage component serves as a container for different flow type pages
 * with navigation handled through the sidebar
 *
 * @component
 */
const FlowsPage: React.FC = () => {
  const [activeFlow, setActiveFlow] = useState<FlowType>('engage');

  const labelClass = 'text-white group-hover:text-gray-900 dark:group-hover:text-white';
  const iconClass = 'text-white group-hover:text-gray-900 dark:group-hover:text-white';

  const sidebarItems = React.useMemo(
    () => [
      {
        label: <span className={labelClass}>Engage</span>,
        icon: <Users size={20} className={iconClass} />,
        href: '#engage',
        isActive: activeFlow === 'engage',
      },
      {
        label: <span className={labelClass}>Convert</span>,
        icon: <Repeat size={20} className={iconClass} />,
        href: '#convert',
        isActive: activeFlow === 'convert',
      },
      {
        label: <span className={labelClass}>Revive</span>,
        icon: <Zap size={20} className={iconClass} />,
        href: '#revive',
        isActive: activeFlow === 'revive',
      },
    ],
    [activeFlow]
  );

  // Handle hash changes
  React.useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') as FlowType;
      if (hash && ['engage', 'convert', 'revive'].includes(hash)) {
        setActiveFlow(hash as FlowType);
      }
    };

    // Set initial flow based on hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const renderFlowPage = () => {
    switch (activeFlow) {
      case 'engage':
        return <EngageFlowPage />;
      case 'convert':
        return <ConvertFlowPage />;
      case 'revive':
        return <ReviveFlowPage />;
      default:
        return <EngageFlowPage />;
    }
  };

  const sidebar = (
    <Sidebar
      items={sidebarItems}
      className="h-full border-0 header-surface backdrop-blur-2xl text-white"
      defaultCollapsed={true}
    />
  );

  return (
    <PageLayout title="Flows" sidebar={sidebar}>
      {renderFlowPage()}
    </PageLayout>
  );
};

export default FlowsPage;
